import {
  EventFilters,
  SchemaDeclaration,
  SystemEventTypes,
} from '@oneriver/types';
import { TridentArtifactTypeV2 } from '@trident/data-access-artifact';
import type { ReactElement } from 'react';

// Declare a recursive type for deep partial, allowing all nested properties to be optional
export type DeepPartial<T> = T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> }
  : T;

/**
 * Define the SourceInfo interface locally since it's no longer exported from @oneriver/types v1.0.216+
 * In the newer versions of @oneriver/types, the EventStream related files are commented out in the index.d.ts
 * This interface maintains compatibility with the structure used in earlier versions and throughout the codebase
 */
export interface SourceInfo {
  eventGroupType: string;
  eventTypeName?: string;
  scope:
    | 'Tenant'
    | 'Capacity'
    | 'Domain'
    | 'Workspace'
    | 'Artifact'
    | 'SubArtifact';
  capacityId?: string;
  /**
   * Display name of the capacity identified by {@link capacityId}. Presentation
   * only — it is never sent to the backend, but it lets the UI (create-rule
   * footer, get-events wizard) name the capacity instead of showing a GUID.
   */
  capacityName?: string;
  tenantId?: string;
  artifactId?: string;
  subArtifactId?: string; // Sub artifact id for the source. I.e. For Business events, the sub artifact is EventSchema item
  subArtifactDisplayName?: string;
  workspaceId?: string;
  domainId?: string;
  eventMetadata?: {
    eventCategory?: 'AzureEvents' | 'FabricSystemEvents' | 'BusinessEvents';
  };
}

import * as CronFrequency from './constants/cron-frequencies';
export type { KqlQueryParameter } from './aria-schema';
export * from './constants/cron-frequencies';
export * from './utils/cron-frequencies';

import {
  CommonCronBasedNamedEventTimeSettings,
  CommonCronBasedQueryParameter,
  KqlQueryParameter,
} from './aria-schema';
/**
 * Result params which will return to the caller when TriggerPane is closed
 */

export type TriggerPaneResult =
  | TriggerPaneSuccessResult
  | TriggerPaneErrorResult;

export interface TriggerPaneSuccessResult {
  redirectionUrl: string; // pointing to the created trigger page in Reflex UI
  reflexId: string;
  triggerId: string;
  startTrigger: boolean; // trigger status
}

export interface TriggerPaneErrorResult {
  errorCode: string; // return error code to the caller if trigger creation is failed
}

export interface TriggerPaneInputsCommon {
  // What to monitor. The structure and attributes of the event data, such as event columns, dimensions, measures and conditions.
  // Optional for Data Pipeline since GetEvent wizard will display all available events
  // Required for Real-Time Hub when using specific Fabric/Azure eventGroupType, as this data
  // is necessary for the GetEvent wizard to navigate directly to the appropriate event selection step
  monitorDetails?: MonitorDetails;
  // Default to OnEachEvent - trigger fires on each event occurrence.
  // Extended to also support pre-populating operator, value, range, and occurrence defaults.
  conditionDetails?: ConditionDetails;
  // Preferred action which trigger will use
  // Default to Email, recipient to current user
  actionDetails?: ActionDetails;
  // Preferred location to save the Reflex artifact and trigger.
  // Default to My workspace and first available artifact. If no artifact, default to Create a new one.
  // Set createNewActivator to true to always default to creating a new activator.
  locationDetails?: LocationDetails;
}

/**
 * Params which the caller can pass when TriggerPane is opened
 */
export interface TriggerPaneInputs extends TriggerPaneInputsCommon {
  // Preferred UI settings to control the visibile sections and options
  uiSettings?: UISettings;
  // Certified events
  telemetry?: Telemetry;
}

export type MonitorDetails =
  | StreamDetails // Event Stream
  | EventSourceDetails // Fabric System events and Azure events, [TODO] deprecate this when v1 is removed
  | EventSubscriptionDetail // Event Subscription including single event detail from Fabric events, Azure events and Business events.
  | EventSubscriptionDetails // Event Subscription including multiple event details from Fabric events, Azure events and Business events.
  | DashboardQueryDetails // RT Dashboard
  | QuerySetQueryDetails // KQL QuerySet
  | DatabaseHubQueryDetails // Database Hub (KQL query over Database Watcher data)
  | WarehouseQueryDetails // Data Warehouse SQL query
  | DigitalTwinBuilderQueryDetails; // Digital Twin Builder Graph query

export interface ConditionDetails {
  conditionType?: ConditionType;

  /** Pre-selected measure column name (applicable for OnEachEventWhen and OnEachEventGroupedBy) */
  measureColumn?: string;

  /** Pre-selected dimension/grouping column name (applicable for OnEachEventGroupedBy) */
  dimensionColumn?: string;

  /** Default operator (e.g., 'IsAbove', 'Changes', 'EntersRange').
   *  Must be a valid operator for the selected conditionType. */
  operator?: string;

  /** Default threshold/comparison value as a string (e.g., '100', 'true') */
  value?: string;

  /** For conditions that require multiple values, such as 'Range' operator with min and max params. */
  values?: string[];

  /** For range operators (EntersRange, ExitsRange): the low bound */
  rangeLow?: string;

  /** For range operators (EntersRange, ExitsRange): the high bound */
  rangeHigh?: string;

  /** For trend operators (IncreasesBy, DecreasesBy, ChangesBy): whether the
   *  offset is expressed as a percentage instead of an absolute value. */
  inPercent?: boolean;

  /** Default occurrence selection for grouped-by conditions (e.g., 'EachTime',
   *  'ForNthTime', 'SustainedPeriodOption'). */
  occurrenceKind?: string;

  /** For ForNthTime occurrence: how many times the condition must be met. */
  occurrenceCount?: number;

  /** Elapsed duration in milliseconds. For ForNthTime it bounds the window the
   *  occurrences must fall in; for SustainedPeriodOption it is the period the
   *  condition must stay true. */
  occurrenceDurationMs?: number;
}

export type ActionDetails =
  | EmailAction
  | TeamsAction
  | FabricJobAction
  | TeamsGroupChatAction
  | TeamsChannelAction
  | PowerAutomateAction;

export interface LocationDetails {
  workspaceId?: string; // Preferred workspace id
  reflexId?: string; // Preferred reflex artifact id. Takes precedence over createNewActivator when both are provided.
}

/**
 * Monitoring details
 */
export const SourceType = {
  Stream: 'Stream',
  SQL: 'SQL',
  QuerySet: 'QuerySet',
  Dashboard: 'Dashboard',
  DatabaseHub: 'DatabaseHub', // Database Hub (KQL query over Database Watcher data)
  EventSubscription: 'EventSubscription',
  Eventhouse: 'Eventhouse', // KQL
  DigitalTwinBuilder: 'DigitalTwinBuilder',
  PBIMetric: 'PBIMetric', // Power BI metrics
  // [TODO]: deprecate SystemEvents and AzureEvents when v1 is removed
  SystemEvents: 'SystemEvents',
  AzureEvents: 'AzureEvents',
} as const;
export type SourceType = (typeof SourceType)[keyof typeof SourceType];

export interface StreamDetails extends BasicMonitorDetails {
  sourceType: typeof SourceType.Stream;
  eventStreamCapacityId: string;
  eventStreamId: string;
  eventStreamWorkspaceId: string;
  eventStreamDisplayName: string;
  streamId?: string; // the selected stream in the ES artifact to be used as data source to Reflex
  streamDisplayName?: string;
  eventType: SchemaDeclaration[];
  timeAxisColumnName?: string;
  /**
   * Optional map of activator (reflex artifact) IDs to stream IDs.
   * When provided for ES onramp, selecting an activator will use this mapping
   * to generate a deterministic source ID from activatorId + streamId.
   * Key: activator/reflex artifact ID, Value: streamId
   */
  activatorStreamIdMap?: Record<string, string>;
  /**
   * When true, generate deterministic source ID using uuidV5(streamId, activatorId).
   * Used for generic node onramp where activator selection determines source ID.
   * When false/undefined, use streamId directly as source ID (activator node flow).
   */
  useActivatorBasedSourceId?: boolean;
}

export interface EventSourceDetails extends BasicMonitorDetails {
  sourceType: typeof SourceType.SystemEvents | typeof SourceType.AzureEvents; // If consumer only pass sourceType, render the list of systemEvents and let user browser and choose
  eventType: SchemaDeclaration[];
  systemEventType: SystemEventTypes;
  eventLabel?: string; // Label for source button in Monitor section.
  containerName?: string; // Label for Container created in Activator modeling
  // required info to create EventSubscription
  sourceInfo: SourceInfo;
  filtersInfo: EventFilters;
}

// Event Subscription including single event detail from Fabric events, Azure events and Business events.
// This is currently used for existing Fabric events and Azure events.
// [TODO] migrate to EventSubscriptionDetails and deprecate this when Fabric/Azure events need to support multiple event types selection.
export interface EventSubscriptionDetail {
  sourceType: typeof SourceType.EventSubscription;
  systemEventType: SystemEventTypes;
  eventType?: SchemaDeclaration[];
  // required info to create EventSubscription
  sourceInfo: SourceInfo;
  filtersInfo: EventFilters;
  containerName?: string; // Label for Container created in Activator modeling
}

// Event Subscription including multiple event details from Fabric events, Azure events and Business events.
export interface EventSubscriptionDetails {
  sourceType: typeof SourceType.EventSubscription;
  containerName?: string; // Label for Container created in Activator modeling
  eventLabel?: string; // Label for source button in Monitor section.
  systemEventType: SystemEventTypes;
  filtersInfo: EventFilters;
  eventDetails: EventDetail[];
}

export interface KustoQueryDetails extends BasicMonitorDetails {
  databaseArtifactId: string; // data source for Kusto querySet and dashboard
  clusterHost: string; // Kusto cluster connection URI, eg, pbiclients.eastus.kusto.windows.net
  databaseName: string;
  queryDefinition: string; // Query body to pull the data
  queryParameters: KqlQueryParameter[];
  cronFrequency?: string; // Default to 300. The frequency with which the query shall be executed in seconds
  eventTimeSettings?: EventTimeSettings; // Reflex watermarks the data based on time stamp for pull and asks for the latest in subsequent calls.
  dimensionColumns: ColumnDefinition[];
  measureColumns: ColumnDefinition[];
  /**
   * Optional columns eligible as the event-time / timestamp column. Populates
   * the Monitor section's "Timestamp" dropdown, whose visibility is controlled
   * by `uiSettings.*.monitorSection.timestampDropdown.show`.
   */
  timestampColumns?: ColumnDefinition[];
}

export interface SqlQueryDetails extends BasicMonitorDetails {
  connectionString?: string;
  queryDefinition: string; // SQL Query body to pull the data
  queryParameters?: CommonCronBasedQueryParameter[];
  cronFrequency?: string; // Default to 300. The frequency with which the query shall be executed in seconds
  eventTimeSettings?: CommonCronBasedNamedEventTimeSettings;
  dimensionColumns: ColumnDefinition[];
  measureColumns: ColumnDefinition[];
}

export interface OntologyQueryDetails extends BasicMonitorDetails {
  queryDefinition: string; // Graph Query body to pull the data
  queryParameters?: CommonCronBasedQueryParameter[];
  cronFrequency?: string;
  eventTimeSettings?: CommonCronBasedNamedEventTimeSettings;
  dimensionColumns: ColumnDefinition[];
  measureColumns: ColumnDefinition[];
}

export interface SqlQueryTriggerPaneMetadata {
  description?: string;
  compositeKey?: {
    name: string;
    keys: string[];
  };
  objectName?: string;
  measureName?: string;
  outOfOrderTolerance?: number;
  uniqueIdentifier?: string;
}

export interface DashboardQueryDetails extends KustoQueryDetails {
  sourceType: typeof SourceType.Dashboard;
  sourceDisplayName?: string;
  sourceMetadata: DashboardMetadata;
}

export interface QuerySetQueryDetails extends KustoQueryDetails {
  sourceType: typeof SourceType.QuerySet;
  sourceDisplayName?: string;
  sourceMetadata: QuerySetMetadata;
}

/**
 * Database Hub onramp.
 *
 * The connection details are KQL-based (same as the RT Dashboard / KQL QuerySet
 * onramps), so this extends KustoQueryDetails. The query cross-queries the
 * Database Watcher ADX cluster and the selected SQL database is injected into
 * the query as a HARDCODED query parameter.
 */
export interface DatabaseHubQueryDetails extends KustoQueryDetails {
  sourceType: typeof SourceType.DatabaseHub;
  sourceDisplayName?: string;
  sourceMetadata: DatabaseHubMetadata;
}

export interface WarehouseQueryDetails extends SqlQueryDetails {
  sourceType: typeof SourceType.SQL;
  sourceDisplayName?: string;
  sourceMetadata: WarehouseMetadata | SQLAnalyticsEndpointMetadata;
}

export interface DigitalTwinBuilderQueryDetails extends OntologyQueryDetails {
  sourceType: typeof SourceType.DigitalTwinBuilder;
  sourceDisplayName?: string;
  sourceMetadata: DigitalTwinBuilderMetadata;
}

// required info to create EventSubscription for Fabric/Azure/Business events
export interface EventDetail {
  sourceInfo: SourceInfo;
  schema: SchemaDeclaration;
}

export interface BasicMonitorDetails {
  eventMetaData?: EventMetadata;
}

export interface EventMetadata {
  eps?: number;
  recordSize?: number;
}

export const ColumnDataType = {
  Number: 'Number',
  Boolean: 'Boolean',
  String: 'String',
  Any: 'Any',
} as const;
export type ColumnDataType =
  (typeof ColumnDataType)[keyof typeof ColumnDataType];

export interface ColumnDefinition {
  colName: string;
  dataType: ColumnDataType;
  sampleValue?: number | boolean | string;
  group?: string;
}

export interface QuerySetMetadata {
  querySetId: string;
  querySetName?: string;
  queryId: string;
}

export interface DashboardMetadata {
  dashboardId: string;
  dashboardName?: string;
  visualId: string;
  /** Visual kind of the selected tile (e.g. 'line', 'bar', 'stat'). Telemetry only. */
  visualType?: string;
}

export interface DatabaseHubMetadata {
  /** ARM resource id of the selected SQL database
   *  (Microsoft.Sql/servers/.../databases/...). */
  databaseResourceId: string;
  /** Human readable name of the selected database. */
  databaseName?: string;
}

export interface WarehouseMetadata {
  warehouseId: string;
  queryId?: string;
  workspaceId: string;
}

export interface SQLAnalyticsEndpointMetadata {
  itemId: string;
  queryId?: string;
  workspaceId: string;
}

export interface DigitalTwinBuilderMetadata extends EntityMetadata {
  digitalTwinBuilderId: string;
  workspaceId: string;
}

export interface PBIMetricMetadata {
  reportId: string; // Power BI report ID used to identify the PBI source
}

export interface EntityMetadata {
  entityId?: string; // Entity GUID
  entityName?: string; // Entity display name
  staticProperties?: string[]; // Properties inside the entity with static values
  entityIdParts?: string[]; // Id parts inside the entity
}

/**
 * Condition details
 */

export const ConditionType = {
  OnEachEvent: 'OnEachEvent',
  OnEachEventWhen: 'OnEachEventWhen',
  OnEachEventGroupedBy: 'OnEachEventGroupedBy',
} as const;
export type ConditionType = (typeof ConditionType)[keyof typeof ConditionType];

/**
 * Action details
 */

export const ActionType = {
  Email: 'Email',
  Teams: 'Teams',
  TeamsChannel: 'TeamsChannel',
  TeamsGroupChat: 'TeamsGroupChat',
  FabricJob: 'FabricJob',
  PowerAutomate: 'PowerAutomate',
  BusinessEvent: 'BusinessEvent',
} as const;
export type ActionType = (typeof ActionType)[keyof typeof ActionType];

export interface BasicAction {
  subject?: string; // subject of the email
  headline?: string; // headline of the Email message, title of the Teams message
  message?: string; // optional message body for Email/Teams action
  linkbackURL?: string; // Any URL to embed in the message. Eg: PowerBI report link, Event URL from OneRiver. It can be shown as the link in the message.
  recipients?: string[]; // recipients for Email/Teams action
}

export interface EmailAction extends BasicAction {
  actionType: typeof ActionType.Email;
}

export interface TeamsAction extends BasicAction {
  actionType: typeof ActionType.Teams;
}

export interface TeamsGroupChatAction extends BasicAction {
  actionType: typeof ActionType.TeamsGroupChat;
}

export interface TeamsChannelAction extends BasicAction {
  actionType: typeof ActionType.TeamsChannel;
}

export interface PowerAutomateAction extends BasicAction {
  actionType: typeof ActionType.PowerAutomate;
}

export interface FabricJobAction {
  actionType: typeof ActionType.FabricJob;
  itemType?: string; // Fabric item type. [TODO] Need to be implemented by Trigger pane
  jobType?: string; // Fabric job to be run
  workspaceId?: string; // Where Fabric job to be run
  artifactId?: string;
  subitemId?: string; // Fabric subitem to be run (e.g., function name for UDF)
}

/**
 * UI Settings
 */

export interface UISettings {
  displayOptions?: {
    title?: string;
    subtitle?: string;
    banner?: BannerSettings;
    actionSection?: ActionUISettings;
    conditionSection?: ConditionUISettings;
    locationSection?: LocationUISettings;
    commitSection?: CommitUISettings;
    monitorSection?: MonitorUISettings;
  };
  controlOptions?: {
    startTrigger?: boolean;
  };
}

export interface BannerSettings {
  /**
   * Hide the preview banner, by default it is shown.
   * The preview banner will display the content provided
   * If content is not provided, the default content will be shown.
   * The content can contains link in [display text](http://) format.
   */
  hide?: boolean; // Hides the preview banner
  content?: string; // Custom content to be shown in the preview banner
}

export interface InfoIcon {
  /**
   * Show the info icon for the section, by default it is hidden.
   * Tooltip for the info icon will display the content provided.
   * If content is not provided, the default content will be shown.
   * The content can contains link in [display text](http://) format.
   * */
  show?: boolean; // show the info icon
  content?: string; // custom content to be shown in the info icon
}

/**
 * If all are set as true(hidden), the caller need to provide the actionDetails as the default value
 */
export interface ActionUISettings {
  hide?: boolean; // hide the UI for the whole action section
  allowedActions?: ActionType[]; // if undefined, show all actions
  infoIcon?: InfoIcon; // info icon, hidden by default
}

/**
 * If all are set as true(hidden), the caller need to provide the conditionDetails as the default value
 */
export interface ConditionUISettings {
  /**
   * Hide the UI for the whole condition section
   * Default to OnEachEvent
   * Caller can set the default value in the conditionDetails
   * */
  hide?: boolean;
  allowedConditionTypes?: ConditionType[]; // if undefined, show all supported condition types
  allowedConditionsBySchema?: {
    [key: string]: ConditionType[]; // Allowed conditions for the schema, where the key is the schema name (e.g., "Microsoft.Fabric.Capacity.Summary")
  };
  allowedConditionOperators?: ConditionOperators;
  infoIcon?: InfoIcon; // info icon, hidden by default
  enableFieldGrouping?: boolean; // enable field grouping in field dropdown
}

/**
 * If all are set as true(hidden), the Pane would create the "MyReflex" reflex artifact in "MyWorkspace" workspace by default
 */
export interface LocationUISettings {
  hide?: boolean; // hide the UI for the whole location section
  hideWorkspaceList?: boolean; // hide the UI of workspace selector and default to user preset workspace
  hideArtifactList?: boolean; // hide the UI of artifact selector and default to user preset reflex artifact
  hideArtifactCreation?: boolean; // hide the UI of the option to create a new reflex artifact
  createNewActivator?: boolean; // When true, default to creating a new activator instead of selecting the first existing one. reflexId takes precedence if both are provided.
  infoIcon?: InfoIcon; // info icon, hidden by default
}

export interface CommitUISettings {
  showStartTrigger?: boolean; // show the UI of toggling StartTrigger and default to use preset value of turning Start Trigger on|off
}

export interface MonitorUISettings {
  /** if included, converts the monitor section to a dropdown where the user can select a source to send to the IA Api.
   * The dropdown consists of whatever is passed into the MonitorDetails in monitorDetails and the array assigned here,
   * no need to duplicate whatever is in monitordetails here.
   * Ex. monitorDetails: QuerySet
   * allowedMonitorDetails: [ SystemEvent, Dashboard ]
   * The dropdown will consist of [QuerySet, SystemEvent, Dashboard] with QuerySet selected by default.
   *  AzureEvents and SystemEvents are not supported for this scenario.
   * */
  allowedMonitorDetails?: MonitorDetails[];
  cronFrequencyOptions?: CronFrequency.CronFrequencyLabel[];
  defaultCronFrequency?: CronFrequency.CronFrequencyLabel;
  infoIcon?: InfoIcon; // info icon, hidden by default
  sourceLabel?: string; // custom label for the Source field, defaults to "Source" if not provided
}

/**
 * Telemetry to be emitted as Certified events
 */
export interface Telemetry {
  // Name of the extension calling the pane, if your extension is not listed use onboarding and reach out to the Reflex team
  client: TelemetryClient;
  // Optional scenario to track the user funnel
  scenario?: string;
  // Optional correlation id to track the user session
  clientCorrelationId?: string;
}

export const TelemetryClient = {
  KustoQuerySet: 'KustoQuerySet',
  KustoDashboard: 'KustoDashboard',
  DatabaseHub: 'DatabaseHub',
  OneRiver: 'OneRiver',
  DataPipeline: 'DataPipeline',
  Warehouse: 'Warehouse',
  EventStream: 'EventStream',
  Ontology: 'Ontology',
  OneLakeCatalog: 'OneLakeCatalog',
  WorkspaceHub: 'WorkspaceHub',
  MonitoringHub: 'MonitoringHub',
  Onboarding: 'Onboarding',
  SQLAnalyticsEndpoint: 'SQLAnalyticsEndpoint',
} as const;
export type TelemetryClient =
  (typeof TelemetryClient)[keyof typeof TelemetryClient];

export interface EventTimeSettings {
  timeFieldName: string;
  timeZone?:
    | 'Australia/Darwin'
    | 'Australia/Sydney'
    | 'Australia/Melbourne'
    | 'Asia/Kabul'
    | 'America/Anchorage'
    | 'America/Juneau'
    | 'America/Metlakatla'
    | 'America/Nome'
    | 'America/Sitka'
    | 'America/Yakutat'
    | 'America/Adak'
    | 'Asia/Barnaul'
    | 'Asia/Riyadh'
    | 'Asia/Bahrain'
    | 'Asia/Kuwait'
    | 'Asia/Qatar'
    | 'Asia/Aden'
    | 'Asia/Dubai'
    | 'Asia/Muscat'
    | 'Etc/GMT-4'
    | 'Asia/Baghdad'
    | 'America/Buenos_Aires'
    | 'America/Argentina/La_Rioja'
    | 'America/Argentina/Rio_Gallegos'
    | 'America/Argentina/Salta'
    | 'America/Argentina/San_Juan'
    | 'America/Argentina/San_Luis'
    | 'America/Argentina/Tucuman'
    | 'America/Argentina/Ushuaia'
    | 'America/Catamarca'
    | 'America/Cordoba'
    | 'America/Jujuy'
    | 'America/Mendoza'
    | 'Europe/Astrakhan'
    | 'Europe/Ulyanovsk'
    | 'America/Halifax'
    | 'Atlantic/Bermuda'
    | 'America/Glace_Bay'
    | 'America/Goose_Bay'
    | 'America/Moncton'
    | 'America/Thule'
    | 'Australia/Eucla'
    | 'Asia/Baku'
    | 'Atlantic/Azores'
    | 'America/Scoresbysund'
    | 'America/Bahia'
    | 'Asia/Dhaka'
    | 'Asia/Thimphu'
    | 'Europe/Minsk'
    | 'Pacific/Bougainville'
    | 'America/Regina'
    | 'America/Swift_Current'
    | 'Atlantic/Cape_Verde'
    | 'Etc/GMT+1'
    | 'Asia/Yerevan'
    | 'Australia/Adelaide'
    | 'Australia/Broken_Hill'
    | 'America/Guatemala'
    | 'America/Belize'
    | 'America/Costa_Rica'
    | 'Pacific/Galapagos'
    | 'America/Tegucigalpa'
    | 'America/Managua'
    | 'America/El_Salvador'
    | 'Etc/GMT+6'
    | 'Asia/Almaty'
    | 'Antarctica/Vostok'
    | 'Asia/Urumqi'
    | 'Indian/Chagos'
    | 'Asia/Bishkek'
    | 'Asia/Qostanay'
    | 'Etc/GMT-6'
    | 'America/Cuiaba'
    | 'America/Campo_Grande'
    | 'Europe/Budapest'
    | 'Europe/Tirane'
    | 'Europe/Prague'
    | 'Europe/Podgorica'
    | 'Europe/Belgrade'
    | 'Europe/Ljubljana'
    | 'Europe/Bratislava'
    | 'Europe/Warsaw'
    | 'Europe/Sarajevo'
    | 'Europe/Zagreb'
    | 'Europe/Skopje'
    | 'Pacific/Guadalcanal'
    | 'Antarctica/Casey'
    | 'Pacific/Ponape'
    | 'Pacific/Kosrae'
    | 'Pacific/Noumea'
    | 'Pacific/Efate'
    | 'Etc/GMT-11'
    | 'America/Mexico_City'
    | 'America/Bahia_Banderas'
    | 'America/Merida'
    | 'America/Monterrey'
    | 'America/Chicago'
    | 'America/Winnipeg'
    | 'America/Rainy_River'
    | 'America/Rankin_Inlet'
    | 'America/Resolute'
    | 'America/Matamoros'
    | 'America/Indiana/Knox'
    | 'America/Indiana/Tell_City'
    | 'America/Menominee'
    | 'America/North_Dakota/Beulah'
    | 'America/North_Dakota/Center'
    | 'America/North_Dakota/New_Salem'
    | 'CST6CDT'
    | 'Pacific/Chatham'
    | 'Asia/Shanghai'
    | 'Asia/Hong_Kong'
    | 'Asia/Macau'
    | 'America/Havana'
    | 'Etc/GMT+12'
    | 'Africa/Nairobi'
    | 'Antarctica/Syowa'
    | 'Africa/Djibouti'
    | 'Africa/Asmera'
    | 'Africa/Addis_Ababa'
    | 'Indian/Comoro'
    | 'Indian/Antananarivo'
    | 'Africa/Mogadishu'
    | 'Africa/Dar_es_Salaam'
    | 'Africa/Kampala'
    | 'Indian/Mayotte'
    | 'Etc/GMT-3'
    | 'Australia/Brisbane'
    | 'Australia/Lindeman'
    | 'Europe/Chisinau'
    | 'America/Sao_Paulo'
    | 'Pacific/Easter'
    | 'America/Cancun'
    | 'America/New_York'
    | 'America/Nassau'
    | 'America/Toronto'
    | 'America/Iqaluit'
    | 'America/Montreal'
    | 'America/Nipigon'
    | 'America/Pangnirtung'
    | 'America/Thunder_Bay'
    | 'America/Detroit'
    | 'America/Indiana/Petersburg'
    | 'America/Indiana/Vincennes'
    | 'America/Indiana/Winamac'
    | 'America/Kentucky/Monticello'
    | 'America/Louisville'
    | 'EST5EDT'
    | 'Africa/Cairo'
    | 'Asia/Yekaterinburg'
    | 'Europe/Kiev'
    | 'Europe/Mariehamn'
    | 'Europe/Sofia'
    | 'Europe/Tallinn'
    | 'Europe/Helsinki'
    | 'Europe/Vilnius'
    | 'Europe/Riga'
    | 'Europe/Uzhgorod'
    | 'Europe/Zaporozhye'
    | 'Pacific/Fiji'
    | 'Europe/London'
    | 'Atlantic/Canary'
    | 'Atlantic/Faeroe'
    | 'Europe/Guernsey'
    | 'Europe/Dublin'
    | 'Europe/Isle_of_Man'
    | 'Europe/Jersey'
    | 'Europe/Lisbon'
    | 'Atlantic/Madeira'
    | 'Europe/Bucharest'
    | 'Asia/Nicosia'
    | 'Asia/Famagusta'
    | 'Europe/Athens'
    | 'Asia/Tbilisi'
    | 'America/Godthab'
    | 'Atlantic/Reykjavik'
    | 'Atlantic/St_Helena'
    | 'Africa/Ouagadougou'
    | 'Africa/Abidjan'
    | 'Africa/Accra'
    | 'America/Danmarkshavn'
    | 'Africa/Banjul'
    | 'Africa/Conakry'
    | 'Africa/Bissau'
    | 'Africa/Monrovia'
    | 'Africa/Bamako'
    | 'Africa/Nouakchott'
    | 'Africa/Freetown'
    | 'Africa/Dakar'
    | 'Africa/Lome'
    | 'America/Port-au-Prince'
    | 'Pacific/Honolulu'
    | 'Pacific/Rarotonga'
    | 'Pacific/Tahiti'
    | 'Pacific/Johnston'
    | 'Etc/GMT+10'
    | 'Asia/Calcutta'
    | 'Asia/Tehran'
    | 'Asia/Jerusalem'
    | 'Asia/Amman'
    | 'Europe/Kaliningrad'
    | 'Asia/Seoul'
    | 'Africa/Tripoli'
    | 'Pacific/Kiritimati'
    | 'Etc/GMT-14'
    | 'Australia/Lord_Howe'
    | 'Asia/Magadan'
    | 'America/Punta_Arenas'
    | 'Pacific/Marquesas'
    | 'Indian/Mauritius'
    | 'Indian/Reunion'
    | 'Indian/Mahe'
    | 'Asia/Beirut'
    | 'America/Montevideo'
    | 'Africa/Casablanca'
    | 'Africa/El_Aaiun'
    | 'America/Chihuahua'
    | 'America/Mazatlan'
    | 'America/Denver'
    | 'America/Edmonton'
    | 'America/Cambridge_Bay'
    | 'America/Inuvik'
    | 'America/Yellowknife'
    | 'America/Ojinaga'
    | 'America/Boise'
    | 'MST7MDT'
    | 'Asia/Rangoon'
    | 'Indian/Cocos'
    | 'Asia/Novosibirsk'
    | 'Africa/Windhoek'
    | 'Asia/Katmandu'
    | 'Pacific/Auckland'
    | 'Antarctica/McMurdo'
    | 'America/St_Johns'
    | 'Pacific/Norfolk'
    | 'Asia/Irkutsk'
    | 'Asia/Krasnoyarsk'
    | 'Asia/Novokuznetsk'
    | 'Asia/Pyongyang'
    | 'Asia/Omsk'
    | 'America/Santiago'
    | 'America/Tijuana'
    | 'America/Santa_Isabel'
    | 'America/Los_Angeles'
    | 'America/Vancouver'
    | 'PST8PDT'
    | 'Asia/Karachi'
    | 'America/Asuncion'
    | 'Asia/Qyzylorda'
    | 'Europe/Paris'
    | 'Europe/Brussels'
    | 'Europe/Copenhagen'
    | 'Africa/Ceuta'
    | 'Europe/Madrid'
    | 'Asia/Srednekolymsk'
    | 'Asia/Kamchatka'
    | 'Asia/Anadyr'
    | 'Europe/Samara'
    | 'Europe/Moscow'
    | 'Europe/Kirov'
    | 'Europe/Simferopol'
    | 'America/Cayenne'
    | 'Antarctica/Rothera'
    | 'Antarctica/Palmer'
    | 'America/Fortaleza'
    | 'America/Belem'
    | 'America/Maceio'
    | 'America/Recife'
    | 'America/Santarem'
    | 'Atlantic/Stanley'
    | 'America/Paramaribo'
    | 'Etc/GMT+3'
    | 'America/Bogota'
    | 'America/Rio_Branco'
    | 'America/Eirunepe'
    | 'America/Coral_Harbour'
    | 'America/Guayaquil'
    | 'America/Jamaica'
    | 'America/Cayman'
    | 'America/Panama'
    | 'America/Lima'
    | 'Etc/GMT+5'
    | 'America/La_Paz'
    | 'America/Antigua'
    | 'America/Anguilla'
    | 'America/Aruba'
    | 'America/Barbados'
    | 'America/St_Barthelemy'
    | 'America/Kralendijk'
    | 'America/Manaus'
    | 'America/Boa_Vista'
    | 'America/Porto_Velho'
    | 'America/Blanc-Sablon'
    | 'America/Curacao'
    | 'America/Dominica'
    | 'America/Santo_Domingo'
    | 'America/Grenada'
    | 'America/Guadeloupe'
    | 'America/Guyana'
    | 'America/St_Kitts'
    | 'America/St_Lucia'
    | 'America/Marigot'
    | 'America/Martinique'
    | 'America/Montserrat'
    | 'America/Puerto_Rico'
    | 'America/Lower_Princes'
    | 'America/Port_of_Spain'
    | 'America/St_Vincent'
    | 'America/Tortola'
    | 'America/St_Thomas'
    | 'Etc/GMT+4'
    | 'Asia/Bangkok'
    | 'Antarctica/Davis'
    | 'Indian/Christmas'
    | 'Asia/Jakarta'
    | 'Asia/Pontianak'
    | 'Asia/Phnom_Penh'
    | 'Asia/Vientiane'
    | 'Asia/Saigon'
    | 'Etc/GMT-7'
    | 'America/Miquelon'
    | 'Asia/Sakhalin'
    | 'Pacific/Apia'
    | 'Africa/Sao_Tome'
    | 'Europe/Saratov'
    | 'Asia/Singapore'
    | 'Asia/Brunei'
    | 'Asia/Makassar'
    | 'Asia/Kuala_Lumpur'
    | 'Asia/Kuching'
    | 'Asia/Manila'
    | 'Etc/GMT-8'
    | 'Africa/Johannesburg'
    | 'Africa/Bujumbura'
    | 'Africa/Gaborone'
    | 'Africa/Lubumbashi'
    | 'Africa/Maseru'
    | 'Africa/Blantyre'
    | 'Africa/Maputo'
    | 'Africa/Kigali'
    | 'Africa/Mbabane'
    | 'Africa/Lusaka'
    | 'Africa/Harare'
    | 'Etc/GMT-2'
    | 'Africa/Juba'
    | 'Asia/Colombo'
    | 'Africa/Khartoum'
    | 'Asia/Damascus'
    | 'Asia/Taipei'
    | 'Australia/Hobart'
    | 'Australia/Currie'
    | 'Antarctica/Macquarie'
    | 'America/Araguaina'
    | 'Asia/Tokyo'
    | 'Asia/Jayapura'
    | 'Pacific/Palau'
    | 'Asia/Dili'
    | 'Etc/GMT-9'
    | 'Asia/Tomsk'
    | 'Pacific/Tongatapu'
    | 'Asia/Chita'
    | 'Europe/Istanbul'
    | 'America/Grand_Turk'
    | 'America/Indianapolis'
    | 'America/Indiana/Marengo'
    | 'America/Indiana/Vevay'
    | 'America/Phoenix'
    | 'America/Creston'
    | 'America/Dawson_Creek'
    | 'America/Fort_Nelson'
    | 'America/Hermosillo'
    | 'Etc/GMT+7'
    | 'Etc/GMT-12'
    | 'Pacific/Tarawa'
    | 'Pacific/Majuro'
    | 'Pacific/Kwajalein'
    | 'Pacific/Nauru'
    | 'Pacific/Funafuti'
    | 'Pacific/Wake'
    | 'Pacific/Wallis'
    | 'Etc/GMT-13'
    | 'Pacific/Enderbury'
    | 'Pacific/Fakaofo'
    | 'Etc/UTC'
    | 'Etc/GMT'
    | 'Etc/GMT+2'
    | 'America/Noronha'
    | 'Atlantic/South_Georgia'
    | 'Etc/GMT+8'
    | 'Pacific/Pitcairn'
    | 'Etc/GMT+9'
    | 'Pacific/Gambier'
    | 'Etc/GMT+11'
    | 'Pacific/Pago_Pago'
    | 'Pacific/Niue'
    | 'Pacific/Midway'
    | 'Asia/Ulaanbaatar'
    | 'Asia/Choibalsan'
    | 'America/Caracas'
    | 'Asia/Vladivostok'
    | 'Asia/Ust-Nera'
    | 'Europe/Volgograd'
    | 'Australia/Perth'
    | 'Africa/Lagos'
    | 'Africa/Luanda'
    | 'Africa/Porto-Novo'
    | 'Africa/Kinshasa'
    | 'Africa/Bangui'
    | 'Africa/Brazzaville'
    | 'Africa/Douala'
    | 'Africa/Algiers'
    | 'Africa/Libreville'
    | 'Africa/Malabo'
    | 'Africa/Niamey'
    | 'Africa/Ndjamena'
    | 'Africa/Tunis'
    | 'Etc/GMT-1'
    | 'Europe/Berlin'
    | 'Europe/Andorra'
    | 'Europe/Vienna'
    | 'Europe/Zurich'
    | 'Europe/Busingen'
    | 'Europe/Gibraltar'
    | 'Europe/Rome'
    | 'Europe/Vaduz'
    | 'Europe/Luxembourg'
    | 'Europe/Monaco'
    | 'Europe/Malta'
    | 'Europe/Amsterdam'
    | 'Europe/Oslo'
    | 'Europe/Stockholm'
    | 'Arctic/Longyearbyen'
    | 'Europe/San_Marino'
    | 'Europe/Vatican'
    | 'Asia/Hovd'
    | 'Asia/Tashkent'
    | 'Antarctica/Mawson'
    | 'Asia/Oral'
    | 'Asia/Aqtau'
    | 'Asia/Aqtobe'
    | 'Asia/Atyrau'
    | 'Indian/Maldives'
    | 'Indian/Kerguelen'
    | 'Asia/Dushanbe'
    | 'Asia/Ashgabat'
    | 'Asia/Samarkand'
    | 'Etc/GMT-5'
    | 'Asia/Hebron'
    | 'Asia/Gaza'
    | 'Pacific/Port_Moresby'
    | 'Antarctica/DumontDUrville'
    | 'Pacific/Truk'
    | 'Pacific/Guam'
    | 'Pacific/Saipan'
    | 'Etc/GMT-10'
    | 'Asia/Yakutsk'
    | 'Asia/Khandyga'
    | 'America/Whitehorse'
    | 'America/Dawson'
    | 'Africa/Timbuktu'
    | 'Egypt'
    | 'Africa/Asmara'
    | 'Libya'
    | 'America/Atka'
    | 'US/Aleutian'
    | 'US/Alaska'
    | 'America/Argentina/Buenos_Aires'
    | 'America/Argentina/Catamarca'
    | 'America/Argentina/ComodRivadavia'
    | 'America/Argentina/Cordoba'
    | 'America/Rosario'
    | 'America/Argentina/Jujuy'
    | 'America/Argentina/Mendoza'
    | 'US/Central'
    | 'America/Shiprock'
    | 'Navajo'
    | 'US/Mountain'
    | 'US/Michigan'
    | 'Canada/Mountain'
    | 'Canada/Atlantic'
    | 'Cuba'
    | 'America/Indiana/Indianapolis'
    | 'US/East-Indiana'
    | 'America/Knox_IN'
    | 'US/Indiana-Starke'
    | 'Jamaica'
    | 'America/Kentucky/Louisville'
    | 'US/Pacific'
    | 'Brazil/West'
    | 'Mexico/BajaSur'
    | 'Mexico/General'
    | 'US/Eastern'
    | 'Brazil/DeNoronha'
    | 'America/Nuuk'
    | 'America/Atikokan'
    | 'US/Arizona'
    | 'America/Virgin'
    | 'Canada/Saskatchewan'
    | 'America/Porto_Acre'
    | 'Brazil/Acre'
    | 'Chile/Continental'
    | 'Brazil/East'
    | 'Canada/Newfoundland'
    | 'America/Ensenada'
    | 'Mexico/BajaNorte'
    | 'Canada/Eastern'
    | 'Canada/Pacific'
    | 'Canada/Yukon'
    | 'Canada/Central'
    | 'Asia/Ashkhabad'
    | 'Asia/Dacca'
    | 'Asia/Ho_Chi_Minh'
    | 'Hongkong'
    | 'Asia/Tel_Aviv'
    | 'Israel'
    | 'Asia/Kathmandu'
    | 'Asia/Kolkata'
    | 'Asia/Macao'
    | 'Asia/Ujung_Pandang'
    | 'Europe/Nicosia'
    | 'ROK'
    | 'Asia/Chongqing'
    | 'Asia/Chungking'
    | 'Asia/Harbin'
    | 'PRC'
    | 'Singapore'
    | 'ROC'
    | 'Iran'
    | 'Asia/Thimbu'
    | 'Japan'
    | 'Asia/Ulan_Bator'
    | 'Asia/Kashgar'
    | 'Asia/Yangon'
    | 'WET'
    | 'Atlantic/Faroe'
    | 'Iceland'
    | 'Australia/South'
    | 'Australia/Queensland'
    | 'Australia/Yancowinna'
    | 'Australia/North'
    | 'Australia/Tasmania'
    | 'Australia/LHI'
    | 'Australia/Victoria'
    | 'Australia/West'
    | 'Australia/ACT'
    | 'Australia/Canberra'
    | 'Australia/NSW'
    | 'HST'
    | 'EST'
    | 'MST'
    | 'Etc/GMT+0'
    | 'Etc/GMT-0'
    | 'Etc/GMT0'
    | 'Etc/Greenwich'
    | 'GMT'
    | 'GMT+0'
    | 'GMT-0'
    | 'GMT0'
    | 'Greenwich'
    | 'Etc/UCT'
    | 'Etc/Universal'
    | 'Etc/Zulu'
    | 'UCT'
    | 'UTC'
    | 'Universal'
    | 'Zulu'
    | 'MET'
    | 'EET'
    | 'Europe/Tiraspol'
    | 'Eire'
    | 'Asia/Istanbul'
    | 'Turkey'
    | 'Portugal'
    | 'Europe/Belfast'
    | 'GB'
    | 'GB-Eire'
    | 'W-SU'
    | 'Atlantic/Jan_Mayen'
    | 'CET'
    | 'Poland'
    | 'Antarctica/South_Pole'
    | 'NZ'
    | 'NZ-CHAT'
    | 'Pacific/Chuuk'
    | 'Pacific/Yap'
    | 'Chile/EasterIsland'
    | 'US/Hawaii'
    | 'Pacific/Kanton'
    | 'Kwajalein'
    | 'Pacific/Samoa'
    | 'US/Samoa'
    | 'Pacific/Pohnpei'
    | 'Antarctica/Troll';
}

export interface ConditionOperators {
  onEachEventWhen?: OnEachEventWhenTriggerConditionOperator[];
  onEachEventGroupBy?: OnEachEventGroupedByTriggerConditionOperator[];
}

export type OnEachEventWhenTriggerConditionOperator =
  | 'IsAbove'
  | 'IsAboveOrEqual'
  | 'IsBelow'
  | 'IsBelowOrEqual'
  | 'IsEqual'
  | 'IsNotEqual'
  | 'IsTrue'
  | 'IsFalse';

export type OnEachEventGroupedByTriggerConditionOperator =
  | 'CrossesAbove'
  | 'CrossesAboveOrEqual'
  | 'CrossesBelow'
  | 'CrossesBelowOrEqual'
  | 'ChangesBy'
  | 'DecreasesBy'
  | 'EntersRange'
  | 'ExitsRange'
  | 'IncreasesBy'
  | 'IsAbove'
  | 'IsAboveOrEqual'
  | 'IsBelow'
  | 'IsBelowOrEqual'
  | 'OnEvent'
  | 'OnNoEvent'
  | 'ChangesToFalse'
  | 'ChangesToTrue'
  | 'IsFalse'
  | 'IsTrue'
  | 'Changes'
  | 'ChangesFrom'
  | 'ChangesTo'
  | 'IsEqual'
  | 'IsNotEqual';

/**
 * The following types are for trigger pane v2
 */

// Preferred view of trigger pane initially to be open
export const ViewName = {
  HomeOnly: 'HomeOnly', // Home page of trigger pane mgmt. experience, which would list all rules, but no create rule button
  Home: 'Home', // Home page of trigger pane mgmt. experience, which would list all rules.
  CreateRule: 'CreateRule',
  CreateRuleOnly: 'CreateRuleOnly', // Create rule page of trigger pane mgmt. experience, but no home view
  EditRule: 'EditRule', // Edit rule page of trigger pane mgmt. experience, which enables a user editing their rule definitions.
  SaveLocation: 'SaveLocation', // Save location page of trigger pane mgmt. experience, which enables a user to save their location.
  RuleTemplate: 'RuleTemplate', // Rule template landing page: a first-class entry view (like Home or CreateRule) that shows the rule template picker (e.g. capacity alert templates). Setting this as `viewName` lands on the picker and populates the templates; selecting a template navigates to CreateRule.
} as const;
export type ViewName = (typeof ViewName)[keyof typeof ViewName];

/**
 * Params which the caller can pass when TriggerPane is opened for mgmt. experience
 */
export type TriggerPaneV2Inputs = HomeOnlyViewInputs | ViewInputs;

// Define the input data for trigger pane when only Home view is enabled
export interface HomeOnlyViewInputs extends TriggerPaneInputsCommon {
  // Preferred definition of the view. HomeOnly would only enable Home view in the trigger pane
  viewName: typeof ViewName.HomeOnly;
  ruleListDetails: RulesListDetails;
  // Preferred UI settings to control the visibile sections and options
  uiSettings?: TriggerPaneUISettingsV2;
  // Certified events
  telemetry?: Telemetry;
}

// Define the input data for trigger pane when Home view and CreateRule view are enabled
export interface ViewInputs extends TriggerPaneInputsCommon {
  // Preferred definition of the view.
  // Home would enable Home view as the entry point of the trigger pane
  // CreateRule would enable CreateRule view as the entry point of the trigger pane
  // RuleTemplate would enable RuleTemplate view as the entry point of the trigger pane
  viewName:
    | typeof ViewName.Home
    | typeof ViewName.CreateRule
    | typeof ViewName.CreateRuleOnly
    | typeof ViewName.RuleTemplate;
  // Preferred UI settings to control the visibile sections and options
  uiSettings?: TriggerPaneUISettingsV2;
  // Certified events
  telemetry?: Telemetry;
}

export interface TriggerPaneUISettingsV2 {
  // Decide which view to be enabled
  // Default to enable all views including Home, CreateRule and EditRule
  enabledViews?: ViewName[];
  // Global setting to disable the ability to change save location from the UI across all views
  disableSaveLocation?: boolean;
  homeViewDisplayOptions?: {
    banner?: BannerSettings;
    /**
     * Selects how the Home view lists and manages rules while the sub-artifact
     * feature is enabled. When true or omitted, rules are loaded across
     * Activator items through the sub-artifact API. When false, rules are loaded
     * only from the selected Activator. The trigger pane's feature switch takes
     * precedence and disables sub-artifact behavior when off.
     */
    listRulesFromSubArtifactApi?: boolean;
    rulesListSection?: RulesListUISettingsV2;
    emptyState?: {
      hideAddRuleButton?: boolean; // hide the add rule button
      infoMessage?: string; // WL specific info message to show instead of default info message
    };
  };
  createRuleViewDisplayOptions?: {
    banner?: BannerSettings;
    actionSection?: ActionUISettingsV2;
    conditionSection?: ConditionUISettingsV2;
    locationSection?: LocationUISettingsV2;
    monitorSection?: MonitorUISettingsV2;
    saveSection?: SaveUISettingsV2;
    detailsSection?: DetailsUISettingsV2;
    /**
     * Optional partner owned hook executed before the CreateRule flow when the
     * user hits Create. See {@link CreateRulePreHook} for the contract.
     */
    createRulePreHook?: CreateRulePreHook;
  };
  editRuleViewDisplayOptions?: {
    banner?: BannerSettings;
    actionSection?: ActionUISettingsV2;
    conditionSection?: ConditionUISettingsV2;
    locationSection?: LocationUISettingsV2;
    monitorSection?: MonitorUISettingsV2;
    detailsSection?: DetailsUISettingsV2;
    saveSection?: SaveUISettingsV2;
  };
  ruleTemplateViewDisplayOptions?: {
    templateFilter?: RuleTemplateFilter;
    artifactName?: string; // artifact name to be used as a parameter in localized strings
    /**
     * Explicit templates to show in the RuleTemplate page. When set, these are
     * shown as-is and take precedence over the templates that would otherwise
     * be derived from the onramp and `templateFilter.artifactType`.
     */
    templateIds?: TriggerPaneInputTemplateId[];
    /**
     * Whether template labels include the artifact specific details, e.g.
     * "Automate action when My Pipeline run failed". Defaults to true.
     * Onramps that list templates across item types, such as the Monitoring Hub,
     * should set this to false to get the concise job labels instead, e.g.
     * "Run failure" / "Run success".
     */
    verbose?: boolean;
    /**
     * Hides the action rule templates — the ones that make the monitored item
     * the *target* of the rule rather than its source, e.g. "Trigger My
     * Pipeline run when specific event happens". Defaults to false (shown).
     *
     * Onramps whose entry point is explicitly about alerting on an item, and
     * which therefore must not offer to run that item, set this to true. The
     * flag is deliberately separate from `templateIds`: it lets a partner keep
     * the derived artifact type selection and only drop the action templates,
     * instead of having to enumerate and maintain the full list themselves.
     */
    hideActionTemplates?: boolean;
    /**
     * Customizes the "Create from scratch" card pinned at the bottom of the
     * RuleTemplate view. Every field is optional; omitted fields keep the
     * default strings, and the card is shown unless `hidden` is true.
     */
    createFromScratchCard?: CreateFromScratchCardSettings;
  };
}

/**
 * Partner overrides for the "Create from scratch" card on the RuleTemplate
 * view. The strings are shown as provided, so partners must pass values that
 * are already localized.
 */
export interface CreateFromScratchCardSettings {
  /**
   * Hides the card entirely. Use when the onramp requires a template, so that
   * "from scratch" is not a reachable outcome.
   */
  hidden?: boolean;
  /** Localized title. Defaults to "Create from scratch". */
  title?: string;
  /**
   * Localized description. Defaults to "Configure a custom alert without using
   * a template". Pass an empty string to render the card with a title only.
   */
  description?: string;
  /**
   * Source the CreateRule view lands on when the user picks "Create from
   * scratch".
   *
   * Same shape as the top level {@link TriggerPaneInputsCommon.monitorDetails},
   * because it is used the same way: picking the card sets the trigger pane
   * input's `monitorDetails` to this value.
   *
   * Without it, "from scratch" carries only whatever the partner already put in
   * the top level `monitorDetails` — for most onramps nothing — so the Monitor
   * section's Source is unset and the get-events wizard opens on its home tab
   * listing *every* event group. Setting this seeds a concrete source (e.g. Job
   * events scoped to a specific Pipeline), so the Source is pre-filled and the
   * wizard opens directly on that group's connector with the item already
   * selected.
   *
   * When set, it replaces the top level `monitorDetails` for this path rather
   * than merging into it: the partner's original source and this one are
   * alternatives, and deep merging them would leave fields of both on one
   * object — a source that is neither.
   */
  monitorDetails?: MonitorDetails;
}

/**
 * Worker action the trigger pane executes *before* the regular CreateRule flow
 * when the user hits Create. The hook owns resolving (and if needed
 * provisioning) the Activator the rule is saved into.
 *
 * The trigger pane sends a {@link CreateRulePreHookPayload} and expects a
 * {@link CreateRulePreHookResponse}: `status: 'Ready'` makes the returned
 * activator the save location and the normal CreateRule flow continues, any
 * other status aborts the create and shows `message` to the user.
 *
 * See `apps/reflex-trigger-pane-extension/docs/features/create-rule-pre-hook/README.md`
 * for the integration example.
 */
export interface CreateRulePreHook {
  /** Worker action to execute, e.g. `monitor.wsmOnboardCheck`. */
  action: string;
  /** Extension which owns the action. Optional when the action is host wide. */
  extensionName?: string;
}

/** Payload the trigger pane sends to the create rule pre hook. */
export interface CreateRulePreHookPayload {
  /** Object id of the workspace currently selected as the save location. */
  workspaceObjectId: string;
}

export const CreateRulePreHookStatus = {
  /** The activator is ready, continue with the CreateRule flow. */
  Ready: 'Ready',
  /** The user is not allowed to complete the onboarding step. */
  InsufficientPermissions: 'InsufficientPermissions',
  /** The payload sent to the hook was rejected. */
  InvalidRequest: 'InvalidRequest',
  /** The hook failed for any other reason. */
  Failed: 'Failed',
} as const;
export type CreateRulePreHookStatus =
  (typeof CreateRulePreHookStatus)[keyof typeof CreateRulePreHookStatus];

export interface CreateRulePreHookSuccessResponse {
  status: typeof CreateRulePreHookStatus.Ready;
  activator: {
    /** Activator (Reflex) item id the rule must be created in. */
    artifactId: string;
    /** Object id of the workspace which owns the activator. */
    workspaceObjectId: string;
  };
}

export interface CreateRulePreHookErrorResponse {
  status: Exclude<
    CreateRulePreHookStatus,
    typeof CreateRulePreHookStatus.Ready
  >;
  /** Localized error message shown to the user. */
  message: string;
}

export type CreateRulePreHookResponse =
  | CreateRulePreHookSuccessResponse
  | CreateRulePreHookErrorResponse;

export interface RulesListUISettingsV2 {
  // Define the fields for filtering the rules list
  // By default, the rules list will always be filtered by the source metadata
  filter?: RulesListUISettingsV2Filter;
  /**
   * Pre-selects the Activator (Item) filter on the Home page with this
   * Activator id. Combine with {@link disableActivatorFilter} to pin the list
   * to a single Activator.
   *
   * Only applies when rules are listed across Activator items via the
   * sub-artifact API; the Home filters are not rendered otherwise.
   */
  activatorId?: string;
  /**
   * Disables the Activator (Item) filter on the Home page, so the user cannot
   * change the selection. Typically paired with {@link activatorId}.
   *
   * Only applies when rules are listed across Activator items via the
   * sub-artifact API; the Home filters are not rendered otherwise.
   */
  disableActivatorFilter?: boolean;
  /**
   * Disables the Workspace filter on the Home page, so the user cannot change
   * the selection. Typically paired with {@link workspaceId}.
   *
   * Only applies when rules are listed across Activator items via the
   * sub-artifact API; the Home filters are not rendered otherwise.
   */
  disableWorkspaceFilter?: boolean;
  /**
   * Pre-selects the Workspace filter on the Home page with this workspace id.
   * Combine with {@link disableWorkspaceFilter} to pin the list to a single
   * workspace.
   *
   * Only applies when rules are listed across Activator items via the
   * sub-artifact API; the Home filters are not rendered otherwise.
   */
  workspaceId?: string;
}

export type RulesListUISettingsV2Filter = ArtifactFilter;
export interface ArtifactFilter {
  artifactType: TridentArtifactTypeV2;
  artifactId: string;
}

/**
 * Defines the metadata for Trigger pane to fetch the list of rules
 *
 */
export type RulesListDetails =
  | StreamRulesListDetails
  | EventSubscriptionRulesListDetails
  | EventhouseRulesListDetails
  | RTDashboardRulesListDetails
  | KqlQuerySetRulesListDetails
  | DatabaseHubRulesListDetails;

export type SourceMetadata =
  | StreamMetadata
  | EventSubscriptionMetadata
  | EventhouseMetadata
  | RTDashboardMetadata
  | KqlQuerySetMetadata
  | DatabaseHubMetadata
  | WarehouseMetadata
  | SQLAnalyticsEndpointMetadata
  | DigitalTwinBuilderMetadata
  | PBIMetricMetadata;

// Default to list all rules for selected eventstream
// If streamId is provided, list all rules for selected stream
export interface StreamRulesListDetails {
  sourceType: typeof SourceType.Stream;
  sourceMetadata: StreamMetadata;
}

export interface StreamMetadata {
  eventstreamId: string; // EventStream artifact ID
  streamId: string; // Selected stream within the EventStream
}

// Default to list all rules for selected event type
// If pipelineId is provided, list all rules for selected event type and selected data pipeline
export interface EventSubscriptionRulesListDetails {
  sourceType: typeof SourceType.EventSubscription;
  sourceMetadata: EventSubscriptionMetadata;
}
export interface EventSubscriptionMetadata {
  systemEventType: SystemEventTypes;
  sourceInfo: SourceInfo;
}

// Default to list all rules for selected eventhouse
// If databaseId is provided, list all rules for selected databaseId
export interface EventhouseRulesListDetails {
  sourceType: typeof SourceType.Eventhouse;
  sourceMetadata: EventhouseMetadata;
}
export interface EventhouseMetadata {
  eventhouseId: string;
  databaseId?: string; // the database id in the Eventhouse
}

// Default to list all rules for selected realtime dashboard
// If visualId is provided, list all rules for selected visualId
export interface RTDashboardRulesListDetails {
  sourceType: typeof SourceType.Dashboard;
  sourceMetadata: RTDashboardMetadata;
}
export interface RTDashboardMetadata {
  dashboardId: string;
  visualId?: string;
  /** Visual kind of the selected tile (e.g. 'line', 'bar', 'stat'). Telemetry only. */
  visualType?: string;
}

// Default to list all rules for selected queryset
// If queryId is provided, list all rules for selected queryId
export interface KqlQuerySetRulesListDetails {
  sourceType: typeof SourceType.QuerySet;
  sourceMetadata: KqlQuerySetMetadata;
}
export interface KqlQuerySetMetadata {
  querySetId: string;
  queryId?: string; // the tab id of the querySet
}

// Default to list all rules for the selected Database Hub source
// If databaseResourceId is provided, list all rules for that database
export interface DatabaseHubRulesListDetails {
  sourceType: typeof SourceType.DatabaseHub;
  sourceMetadata: DatabaseHubMetadata;
}

/**
 * UISettingsV2
 */
export interface ActionUISettingsV2 {
  allowedActions?: ActionType[]; // if undefined, show all actions
  infoIcon?: InfoIcon; // info icon, hidden by default
  hide?: boolean; // hide the UI for the whole action section
}

export interface DetailsUISettingsV2 {
  infoIcon?: InfoIcon; // info icon, hidden by default
  ruleDisplayName?: string; // custom rule name to be used as default in CreateRule page
}

export interface ConditionUISettingsV2 {
  /**
   * Hide the UI for the whole condition section
   * Default to all supported conditions
   * Caller can set the default value in the conditionDetails
   * */
  hide?: boolean;
  allowedConditionTypes?: ConditionType[]; // if undefined, show all supported condition types
  allowedConditionOperators?: ConditionOperators;
  allowedConditionsBySchema?: {
    [key: string]: ConditionType[]; // Allowed conditions for the schema, where the key is the schema name (e.g., "Microsoft.Fabric.Capacity.Summary")
  };
  infoIcon?: InfoIcon; // info icon, hidden by default
  enableFieldGrouping?: boolean; // Enable grouping for measure columns
  independentGroupSelection?: boolean; // When true, selecting a field in one group doesn't select it in other groups
  onChange?: ConditionUIOnChangeEventHandler; // Fired when the condition changes
}

// Worker action that would be defined from any partner team. It will be fired as Callback function when the condition UI changes
export interface ConditionUIOnChangeEventHandler {
  action: string;
  extensionName: string;
}

export type ModelConditionDetails =
  | OnEachEventConditionDetails
  | OnEachEventWhenConditionDetails
  | OnEachEventGroupedByConditionDetails;

export interface OnEachEventConditionDetails {
  conditionType: typeof ConditionType.OnEachEvent;
}

export interface OnEachEventWhenConditionDetails {
  conditionType: typeof ConditionType.OnEachEventWhen;
  measureColumn: string;
}

export interface OnEachEventGroupedByConditionDetails {
  conditionType: typeof ConditionType.OnEachEventGroupedBy;
  dimensionColumn: string;
  measureColumn: string;
}

// Payload that would be sent to the partner team (i.e. DTB UI) when the condition UI changes
export interface ConditionUIOnChangeEventHandlerPayload {
  modelCondition?: ModelConditionDetails;
  sourceMetadata?: SourceMetadata;
}

// Result returned from partner team (i.e. DTB UI) with newer payload to update trigger pane state.
export type ConditionUIOnChangeEventHandlerResponse =
  DigitalTwinBuilderOnChangeEventHandlerResponse;

// Response from DTB UI when the condition UI changes
export interface DigitalTwinBuilderOnChangeEventHandlerResponse {
  queryDefinition: string; // query body
  timeFieldColumn?: string; // Time series column name
}

export interface LocationUISettingsV2 {
  createNewActivator?: boolean; // When true, default to creating a new activator instead of selecting the first existing one. reflexId takes precedence if both are provided.
  infoIcon?: InfoIcon; // info icon, hidden by default
}

export interface MonitorUISettingsV2 {
  infoIcon?: InfoIcon; // info icon, hidden by default
  cronFrequencyOptions?: CronFrequency.CronFrequencyLabel[];
  defaultCronFrequency?: CronFrequency.CronFrequencyLabel;
  allowedMonitorDetails?: MonitorDetails[];
  sourceLabel?: string; // custom label for the Source field, defaults to "Source" if not provided
  /**
   * Custom label for the final (completion) button on the "Review + connect" step
   * of the Get events wizard, which is launched from the Source button.
   * When not provided, the wizard falls back to its own default ("Save" or "Finish").
   */
  successCloseButtonLabel?: string;
  /**
   * Expand the "Show/Hide event types" accordion when the Monitor section first
   * renders. Defaults to false (collapsed). The user can still toggle it either way.
   */
  expandEventTypesByDefault?: boolean;
  /**
   * Source dropdown settings. When enabled, the Monitor section lets the user
   * pick the rule's source from a list fetched from the consumer, instead of
   * showing the read-only source label or the "Select source event" button.
   */
  sourceDropdown?: SourceDropdownUISettings;
  /**
   * Timestamp dropdown settings. Hidden by default. Set it under both
   * `createRuleViewDisplayOptions` and `editRuleViewDisplayOptions` to show the
   * dropdown in both views — they are resolved independently.
   */
  timestampDropdown?: TimestampDropdownUISettings;
}

/** Enables the Timestamp dropdown of the Monitor section. */
export interface TimestampDropdownUISettings {
  /** Show the Timestamp dropdown. By default it is hidden. */
  show?: boolean;
}

/**
 * Enables and configures the Source dropdown of the Monitor section.
 *
 * When enabled, the trigger pane executes `fetchSourcesAction` on the consumer's
 * extension to load the selectable sources. The value of the option the user
 * picks is written into the rule (for KQL onramps it is injected into the query
 * as a HARDCODED query parameter).
 */
export interface SourceDropdownUISettings {
  /** Show the Source dropdown, by default it is hidden. */
  show?: boolean;
  /**
   * Extension action the trigger pane executes to fetch the selectable sources.
   * Database Hub supplies `getDbHubDatabaseInventory` and its extension name.
   */
  fetchSources?: SourceDropdownFetchSourcesHandler;
  /**
   * Value of the source the dropdown defaults to (e.g. an ARM resource id).
   * When set, it is selected automatically as long as the rule does not already
   * have a source and the onramp metadata does not provide one to pre-select.
   */
  defaultOption?: string;
  /** Grey out the Source dropdown so the user cannot change the source. Defaults to false. */
  disabled?: boolean;
}

// Worker action that would be defined from any partner team. It is executed to load the Source dropdown options.
export interface SourceDropdownFetchSourcesHandler {
  action: string;
  extensionName: string;
}

/** A source offered by the Source dropdown. */
export interface SourceOption {
  /** Value persisted on the rule when the option is selected (e.g. an ARM resource id). */
  value: string;
  /** Human readable name shown in the dropdown. */
  displayName: string;
}

export interface SourceOptionsResponse {
  sources: SourceOption[];
}

export interface GetDbHubDatabaseInventoryRequest {
  databaseTypes?: string[] | null;
}

/** Supported request payloads for Source dropdown inventory actions. */
export type FetchSourceOptionsRequest = GetDbHubDatabaseInventoryRequest;

export interface DbHubDatabaseSummary {
  name: string;
  resourceId?: string;
}

export interface GetDbHubDatabaseInventoryResponse {
  databases: DbHubDatabaseSummary[];
  isLoadComplete: boolean;
  loadedCount: number;
  totalCount?: number;
  lastUpdatedAt?: string;
  errorMessage?: string;
}

/** Supported response shapes for Source dropdown inventory actions. */
export type FetchSourceOptionsResponse =
  | SourceOptionsResponse
  | GetDbHubDatabaseInventoryResponse;

export interface SaveUISettingsV2 {
  infoIcon?: InfoIcon; // info icon, hidden by default
  collapsible?: boolean; // make save location collapsible
}

export interface RuleTemplateFilter {
  artifactId: string;
  artifactType: TridentArtifactTypeV2;
}

// Input template which is used in RuleTemplate view
export interface TriggerPaneInputTemplate {
  input: DeepPartial<TriggerPaneV2Inputs>;
  // template metadata
  id: TriggerPaneInputTemplateId;
  displayName: string;
  description?: string;
  icon?: string | ReactElement;
}

export type TriggerPaneInputTemplateId =
  | 'CapacityMetricThresholdTemplate'
  | 'CapacityStateChangeTemplate'
  | 'CapacityUsageThresholdTemplate'
  | 'KustoDatabaseFileFolderCreatedTemplate'
  | 'KustoDatabaseFileFolderDeletedTemplate'
  | 'LakehouseFileFolderCreatedTemplate'
  | 'LakehouseFileFolderDeletedTemplate'
  | 'MountedRelationalDatabaseFileFolderCreatedTemplate'
  | 'MountedRelationalDatabaseFileFolderDeletedTemplate'
  | 'PipelineJobFailedTemplate'
  | 'PipelineJobSuccessTemplate'
  | 'RunPipelineActionTemplate'
  | 'RunSparkJobDefinitionActionTemplate'
  | 'RunSynapseNotebookActionTemplate'
  | 'SQLDbNativeFileFolderCreatedTemplate'
  | 'SQLDbNativeFileFolderDeletedTemplate'
  | 'SparkJobDefinitionJobFailedTemplate'
  | 'SparkJobDefinitionJobSuccessTemplate'
  | 'SynapseNotebookJobFailedTemplate'
  | 'SynapseNotebookJobSuccessTemplate'
  | 'WarehouseFileFolderCreatedTemplate'
  | 'WarehouseFileFolderDeletedTemplate'
  // Database Hub (KQL) alert templates backed by Database Watcher data
  | 'DatabaseHighCpuTemplate'
  | 'DatabaseHighTransactionLogUtilizationTemplate'
  | 'DatabaseDeadlockSpikeTemplate';

/**
 * ============================================================================
 * Rule Insights contract (`open.rtp.ruleinsights`)
 * ============================================================================
 *
 * Rule Insights is a trigger-pane surface (a lightweight popover / embeddable
 * iframe) that shows a single rule's title, health, activation-trend chart and
 * details. Partner teams (e.g. RTH / Onramp) embed it by executing the
 * `open.rtp.ruleinsights` action on the `rtp` extension and passing the rule's
 * ids below — they never build or pass the fully-shaped presentational data; the
 * trigger-pane extension fetches the rule's real facts with its own auth and
 * returns the iframe metadata the partner renders.
 *
 * @example
 * ```ts
 * import { RuleInsightsInputs, RuleInsightsResult } from '@reflex/trigger-pane-types';
 *
 * const data: RuleInsightsInputs = { ruleId, workspaceId, reflexId };
 * const { result } = await extensionClient.action.execute<
 *   RuleInsightsInputs,
 *   RuleInsightsResult
 * >({ extensionName: 'rtp', action: 'open.rtp.ruleinsights', data });
 * // result.iframeUrl -> render <iframe src={result.iframeUrl}> on your surface.
 * ```
 */

/**
 * The `data` payload a partner team sets when executing the
 * `open.rtp.ruleinsights` action — the shape RTH / Onramp import from
 * `@reflex/trigger-pane-types` and assign to `data`. It is intentionally a
 * small, flat, closed set of ids (no nesting): the extension fetches everything
 * else (rule facts, activation trend) with its own auth.
 *
 * @example
 * ```ts
 * import { RuleInsightsInputs } from '@reflex/trigger-pane-types';
 *
 * const data: RuleInsightsInputs = { ruleId, workspaceId, reflexId };
 * await extensionClient.action.execute({
 *   extensionName: 'rtp',
 *   action: 'open.rtp.ruleinsights',
 *   data,
 * });
 * ```
 */
export interface RuleInsightsInputs {
  /** The rule whose insights to show. Required — enough on its own to load the
   * iframe (the component shows demo data until real data is available). */
  ruleId: string;
  /**
   * Fabric workspace id that owns the Reflex (Activator) artifact. Send together
   * with `reflexId` for the extension to fetch the rule's REAL data; omit both
   * to show demo data.
   */
  workspaceId?: string;
  /**
   * The Reflex (Activator) artifact id that owns the rule. Send together with
   * `workspaceId` for the extension to fetch the rule's REAL data; omit both to
   * show demo data.
   */
  reflexId?: string;
  /** Optional semantic-model object id. */
  modelObjectId?: string;
  /** Optional human-readable rule name, for display/telemetry. */
  ruleDisplayName?: string;
  /** Optional telemetry context forwarded by the calling surface. */
  telemetry?: Record<string, unknown>;
}

/**
 * The iframe metadata `open.rtp.ruleinsights` returns so a partner can embed
 * Rule Insights on their own surface (Ben Carlson's OneRiver iframe pattern) —
 * the trigger-pane extension does NOT open any UI on its side.
 */
export interface RuleInsightsResult {
  /**
   * Origin (scheme + host + path) the Rule Insights component is served from.
   * Use this as the `targetOrigin` when you `postMessage` into the iframe and to
   * validate `event.origin` on messages you receive back from it.
   */
  origin: string;
  /**
   * A ready-to-use iframe `src`: the component route origin plus the
   * `#rule-insights` hash route carrying the requested `ruleId`. Render
   * `<iframe src={iframeUrl}>`.
   */
  iframeUrl: string;
}

/**
 * ============================================================================
 * Rule Insights iframe postMessage protocol
 * ============================================================================
 *
 * The embedded Rule Insights iframe (served from `RuleInsightsResult.origin`)
 * and the embedding partner surface talk over `window.postMessage`. These are
 * the shared `type` discriminators for that protocol, published here so BOTH
 * the trigger-pane extension (which sends READY/RESIZE/SET_RUNNING and receives
 * FACTS) and any partner host (which receives READY/RESIZE/SET_RUNNING and
 * sends FACTS) reference a single source of truth instead of duplicating magic
 * strings.
 */

/**
 * Sent by the iframe to its parent once it has mounted and is ready to receive
 * data. The parent replies with a `RULE_INSIGHTS_FACTS_MESSAGE`. Payload:
 * `{ type }`.
 */
export const RULE_INSIGHTS_READY_MESSAGE = 'rtp.ruleInsights.ready';

/**
 * Sent by the parent INTO the iframe carrying the rule's real facts (fetched
 * with the host's auth), so the iframe renders real data without needing auth
 * of its own. Payload: `{ type, facts }`.
 */
export const RULE_INSIGHTS_FACTS_MESSAGE = 'rtp.ruleInsights.facts';

/**
 * Sent by the iframe to its parent when the user flips the status toggle, so
 * the parent (which has auth) can start/stop the rule and post back updated
 * facts. Payload: `{ type, isRunning }`.
 */
export const RULE_INSIGHTS_SET_RUNNING_MESSAGE = 'rtp.ruleInsights.setRunning';

/**
 * Sent by the iframe to its parent to report its rendered content height, so
 * the embedding surface can size the iframe to the content instead of guessing
 * a fixed height. Payload: `{ type, height }`.
 */
export const RULE_INSIGHTS_RESIZE_MESSAGE = 'rtp.ruleInsights.resize';

/**
 * Sent by the iframe to its parent AFTER a user-initiated start/stop has
 * actually settled, carrying the CONFIRMED rule state. Payload:
 * `RuleInsightsRuleChangedMessage`.
 *
 * This is the notification counterpart to `RULE_INSIGHTS_SET_RUNNING_MESSAGE`,
 * and hosts should treat the two very differently:
 *
 * - `setRunning` is a **request** ("the user asked to start/stop this rule —
 *   you have auth, please do it"). It fires *optimistically*, the moment the
 *   toggle is flipped, and it is only sent on hosts that drive the handshake
 *   themselves. It may never be followed by a successful mutation.
 * - `ruleChanged` is a **fact** ("this rule is now started/stopped"). It fires
 *   once the mutation has completed and the refreshed status has been applied,
 *   and it fires on **every** host — including hosts that post nothing and let
 *   the iframe self-serve over the same-origin worker bridge, which previously
 *   had no way to learn that a rule's state had changed underneath them.
 *
 * Use it to keep host-side UI in sync (a rules-list row's status pill, a
 * cached rule collection, telemetry) without polling. Because a single host can
 * embed many Rule Insights iframes at once (a rules list renders one per row),
 * the payload carries the rule identity so the host knows *which* rule changed.
 *
 * Failures are reported too (`succeeded: false`) so a host can surface an error
 * or roll back an optimistic update instead of silently diverging.
 */
export const RULE_INSIGHTS_RULE_CHANGED_MESSAGE =
  'rtp.ruleInsights.ruleChanged';

/**
 * Payload of a {@link RULE_INSIGHTS_RULE_CHANGED_MESSAGE}. Emitted only for
 * user-initiated status changes made from inside the Rule Insights card — never
 * for the initial load or a plain data refresh.
 */
export interface RuleInsightsRuleChangedMessage {
  type: typeof RULE_INSIGHTS_RULE_CHANGED_MESSAGE;
  /**
   * The rule the user acted on, as carried on the iframe's hash route. Lets a
   * host that embeds several Rule Insights iframes attribute the change to the
   * right row. Absent only when the embed URL carried no rule id.
   */
  ruleId?: string;
  /** Workspace id from the iframe's hash route, when the embed supplied one. */
  workspaceId?: string;
  /** Reflex (Activator item) id from the hash route, when the embed supplied one. */
  reflexId?: string;
  /**
   * Whether the change was applied. `false` means the start/stop failed (or the
   * refreshed state could not be read back) and the rule is NOT known to be in
   * `isRunning`; the host should not commit an optimistic update.
   */
  succeeded: boolean;
  /**
   * The state the user asked for. Always present, including on failure, so a
   * host can report "couldn't start this rule" precisely.
   */
  requestedIsRunning: boolean;
  /**
   * The CONFIRMED running state read back after the mutation. Only present when
   * `succeeded` is `true`. Prefer this over `requestedIsRunning` when updating
   * host UI — a backend can settle somewhere other than where the user aimed.
   */
  isRunning?: boolean;
  /**
   * The confirmed backend status behind `isRunning` (e.g. `'Running'` /
   * `'Stopped'`), for hosts that render richer status than a boolean. Only
   * present when `succeeded` is `true`.
   *
   * Typed as a string rather than the RMC `RuleStatus` enum so this partner
   * contract stays free of internal SDK dependencies; the values are exactly
   * RMC's `RuleStatus` members.
   */
  backendRunningState?: string;
}
